import React, { useMemo } from "react";
import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import theme from "../../themes";
import { filterControlCss } from "../../components/ContentLoading";
import { formatCount } from "./helpers";
import {
  clampPage,
  getPageRange,
  getVisiblePageItems,
} from "./pagination";

type TablePaginationProps = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onChange: (page: number) => void;
  disabled?: boolean;
};

const TablePagination: React.FC<TablePaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const safePage = clampPage(currentPage, totalPages);
  const items = useMemo(
    () => getVisiblePageItems(safePage, totalPages),
    [safePage, totalPages]
  );
  const range = getPageRange(safePage, pageSize, totalItems);
  const isFirst = safePage <= 1;
  const isLast = safePage >= totalPages;
  const showControls = totalPages > 1;

  const goToPage = (page: number) => {
    if (disabled) return;
    const next = clampPage(page, totalPages);
    if (next !== currentPage) onChange(next);
  };

  return (
    <Bar>
      <RangeText>
        {range.total === 0
          ? t("common.noData")
          : t("reports.pagination.showing", {
              from: formatCount(range.from),
              to: formatCount(range.to),
              total: formatCount(range.total),
            })}
      </RangeText>

      {showControls ? (
        <Nav aria-label={t("reports.pagination.pageOf", {
          page: safePage,
          pages: totalPages,
        })}>
          <Tooltip title={t("reports.pagination.first")}>
            <TipTarget>
              <IconBtn
                type="button"
                aria-label={t("reports.pagination.first")}
                disabled={disabled || isFirst}
                onClick={() => goToPage(1)}
              >
                <DoubleLeftOutlined />
              </IconBtn>
            </TipTarget>
          </Tooltip>
          <Tooltip title={t("reports.pagination.previous")}>
            <TipTarget>
              <IconBtn
                type="button"
                aria-label={t("reports.pagination.previous")}
                disabled={disabled || isFirst}
                onClick={() => goToPage(safePage - 1)}
              >
                <LeftOutlined />
              </IconBtn>
            </TipTarget>
          </Tooltip>

          {items.map((item, index) =>
            item === "ellipsis" ? (
              <Ellipsis key={`ellipsis-${index}`} aria-hidden="true">
                …
              </Ellipsis>
            ) : (
              <PageBtn
                key={item}
                type="button"
                $active={item === safePage}
                aria-current={item === safePage ? "page" : undefined}
                aria-label={t("reports.pagination.goToPage", { page: item })}
                disabled={disabled}
                onClick={() => goToPage(item)}
              >
                {item}
              </PageBtn>
            )
          )}

          <Tooltip title={t("reports.pagination.next")}>
            <TipTarget>
              <IconBtn
                type="button"
                aria-label={t("reports.pagination.next")}
                disabled={disabled || isLast}
                onClick={() => goToPage(safePage + 1)}
              >
                <RightOutlined />
              </IconBtn>
            </TipTarget>
          </Tooltip>
          <Tooltip title={t("reports.pagination.last")}>
            <TipTarget>
              <IconBtn
                type="button"
                aria-label={t("reports.pagination.last")}
                disabled={disabled || isLast}
                onClick={() => goToPage(totalPages)}
              >
                <DoubleRightOutlined />
              </IconBtn>
            </TipTarget>
          </Tooltip>
        </Nav>
      ) : null}
    </Bar>
  );
};

const Bar = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${theme.colors.grey2};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  flex-wrap: wrap;
  min-width: 0;

  @media (max-width: 640px) {
    padding: 12px 16px;
    justify-content: center;
  }
`;

const RangeText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.colors.grey1};
  white-space: nowrap;

  @media (max-width: 640px) {
    white-space: normal;
    text-align: center;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;
  max-width: 100%;
`;

const TipTarget = styled.span`
  display: inline-flex;
`;

const buttonBase = `
  min-width: 34px;
  height: 34px;
  padding: 0 8px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
`;

const IconBtn = styled.button`
  ${buttonBase}
  flex-shrink: 0;
  font-size: 12px;
  border: 1px solid ${theme.colors.grey2};
  background: #fff;
  color: #111827;
  ${filterControlCss}

  &:hover:not(:disabled) {
    border-color: ${theme.colors.green1};
    color: ${theme.colors.green1};
    background: ${theme.colors.green2};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.green1};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${theme.colors.grey1};
    background: ${theme.colors.grey3};
    opacity: 1;
  }
`;

const PageBtn = styled.button<{ $active: boolean }>`
  ${buttonBase}
  flex-shrink: 0;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  border: 1px solid
    ${({ $active }) => ($active ? theme.colors.green1 : theme.colors.grey2)};
  background: ${({ $active }) => ($active ? theme.colors.green1 : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#111827")};
  ${filterControlCss}

  &:hover:not(:disabled) {
    border-color: ${theme.colors.green1};
    color: ${({ $active }) => ($active ? "#fff" : theme.colors.green1)};
    background: ${({ $active }) =>
      $active ? theme.colors.green1 : theme.colors.green2};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.green1};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: ${({ $active }) => ($active ? "default" : "not-allowed")};
    opacity: ${({ $active }) => ($active ? 1 : 0.65)};
  }
`;

const Ellipsis = styled.span`
  min-width: 24px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.grey1};
  letter-spacing: 1px;
  user-select: none;
`;

export default TablePagination;
